/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/services/googleMapsService.ts
 * Release: 2.2.0 - Servizio centralizzato Google Maps con cache ottimizzata
 * Descrizione: Centralizza tutte le chiamate API Google Maps con cache intelligente
 */

import { loadGoogleMaps, isGoogleMapsLoaded } from '../utils/googleMapsLoader';

// Cache centralizzate per tutti i servizi Google Maps
const geocodingCache = new Map<string, { lat: number; lng: number; timestamp: number }>();
const travelTimeCache = new Map<string, { duration: string; distance: string; timestamp: number }>();
const autocompleteCache = new Map<string, { predictions: google.maps.places.AutocompletePrediction[]; timestamp: number }>();
const placeDetailsCache = new Map<string, { place: any; timestamp: number }>();
const reverseGeocodingCache = new Map<string, { address: string; timestamp: number }>();

const CACHE_DURATION = 30 * 60 * 1000; // 30 minuti
const MAX_CACHE_SIZE = 100; // Massimo numero di elementi per cache

// Coordinate di default (sede Coronatek)
const DEFAULT_ADDRESS = {
  lat: 37.5189, // Misterbianco - Via Salvatore Averna, 3
  lng: 15.0094,
  address: 'Sede Coronatek, Via Salvatore Averna, 3, Misterbianco (CT)'
};

// Pulizia automatica cache
const cleanupCache = (cache: Map<string, any>) => {
  const now = Date.now();
  const entries = Array.from(cache.entries());
  
  // Rimuovi elementi scaduti
  entries.forEach(([key, value]) => {
    if (now - value.timestamp > CACHE_DURATION) {
      cache.delete(key);
    }
  });
  
  // Se ancora troppo grande, rimuovi i più vecchi
  if (cache.size > MAX_CACHE_SIZE) {
    const sortedEntries = Array.from(cache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toDelete = sortedEntries.slice(0, cache.size - MAX_CACHE_SIZE);
    toDelete.forEach(([key]) => cache.delete(key));
    
    console.log(`[GoogleMapsService] 🧹 Cache pulita: rimossi ${toDelete.length} elementi`);
  }
};

// Pulizia automatica ogni 10 minuti
setInterval(() => {
  cleanupCache(geocodingCache);
  cleanupCache(travelTimeCache);
  cleanupCache(autocompleteCache);
  cleanupCache(placeDetailsCache);
  cleanupCache(reverseGeocodingCache);
}, 10 * 60 * 1000);

/**
 * Servizio centralizzato Google Maps
 */
export class GoogleMapsService {
  private static instance: GoogleMapsService;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): GoogleMapsService {
    if (!GoogleMapsService.instance) {
      GoogleMapsService.instance = new GoogleMapsService();
    }
    return GoogleMapsService.instance;
  }

  /**
   * Inizializza Google Maps se necessario
   */
  private async ensureInitialized(): Promise<boolean> {
    if (this.isInitialized && isGoogleMapsLoaded()) {
      return true;
    }

    try {
      if (!isGoogleMapsLoaded()) {
        await loadGoogleMaps();
      }
      
      if (isGoogleMapsLoaded()) {
        this.isInitialized = true;
        return true;
      }
      
      throw new Error('Google Maps non caricato');
    } catch (error) {
      console.error('[GoogleMapsService] Errore inizializzazione:', error);
      return false;
    }
  }

  /**
   * Geocodificazione con cache
   */
  async geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
    const cached = geocodingCache.get(address);
    const now = Date.now();
    
    // Controlla cache
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      console.log('[GoogleMapsService] 💾 Geocoding dalla cache:', address);
      return { lat: cached.lat, lng: cached.lng };
    }

    // Inizializza se necessario
    if (!(await this.ensureInitialized())) {
      return null;
    }

    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      
      geocoder.geocode({ address }, (results: any, status: string) => {
        if (status === 'OK' && results[0] && results[0].geometry) {
          const lat = results[0].geometry.location.lat();
          const lng = results[0].geometry.location.lng();
          
          // Salva in cache
          geocodingCache.set(address, { lat, lng, timestamp: Date.now() });
          console.log('[GoogleMapsService] ✅ Geocoding completato e cached:', address);
          
          resolve({ lat, lng });
        } else {
          console.warn('[GoogleMapsService] ⚠️ Geocoding fallito:', status);
          resolve(null);
        }
      });
    });
  }

  /**
   * Autocomplete con cache
   */
  async getAutocompletePredictions(input: string, options?: google.maps.places.AutocompletionRequest): Promise<google.maps.places.AutocompletePrediction[]> {
    const cacheKey = `${input}_${JSON.stringify(options || {})}`;
    const cached = autocompleteCache.get(cacheKey);
    const now = Date.now();
    
    // Controlla cache
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      console.log('[GoogleMapsService] 💾 Autocomplete dalla cache:', input);
      return cached.predictions;
    }

    // Inizializza se necessario
    if (!(await this.ensureInitialized())) {
      return [];
    }

    return new Promise((resolve) => {
      const service = new window.google.maps.places.AutocompleteService();
      
      const request = {
        input,
        types: ['address'],
        componentRestrictions: { country: 'it' },
        ...options
      };

      service.getPlacePredictions(request, (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          // Salva in cache
          autocompleteCache.set(cacheKey, { predictions, timestamp: Date.now() });
          console.log('[GoogleMapsService] ✅ Autocomplete completato e cached:', input);
          
          resolve(predictions);
        } else {
          console.warn('[GoogleMapsService] ⚠️ Autocomplete fallito:', status);
          resolve([]);
        }
      });
    });
  }

  /**
   * Place Details con cache
   */
  async getPlaceDetails(placeId: string, fields?: string[]): Promise<any | null> {
    const cacheKey = `${placeId}_${(fields || []).join(',')}`;
    const cached = placeDetailsCache.get(cacheKey);
    const now = Date.now();
    
    // Controlla cache
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      console.log('[GoogleMapsService] 💾 Place Details dalla cache:', placeId);
      return cached.place;
    }

    // Inizializza se necessario
    if (!(await this.ensureInitialized())) {
      return null;
    }

    return new Promise((resolve) => {
      // Crea un div temporaneo per il PlacesService
      const mapDiv = document.createElement('div');
      const map = new window.google.maps.Map(mapDiv);
      const service = new window.google.maps.places.PlacesService(map);
      
      const request = {
        placeId,
        fields: fields || ['formatted_address', 'address_components', 'geometry']
      };

      service.getDetails(request, (place: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          // Salva in cache
          placeDetailsCache.set(cacheKey, { place, timestamp: Date.now() });
          console.log('[GoogleMapsService] ✅ Place Details completato e cached:', placeId);
          
          resolve(place);
        } else {
          console.warn('[GoogleMapsService] ⚠️ Place Details fallito:', status);
          resolve(null);
        }
      });
    });
  }

  /**
   * Calcolo tempo di percorrenza con cache (dalla sede di default)
   */
  async getTravelTime(originLat: number, originLng: number): Promise<{ duration: string; distance: string } | null> {
    const cacheKey = `${originLat.toFixed(4)},${originLng.toFixed(4)}`;
    const cached = travelTimeCache.get(cacheKey);
    const now = Date.now();
    
    // Controlla cache
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      console.log('[GoogleMapsService] 💾 Travel Time dalla cache:', cacheKey);
      return { duration: cached.duration, distance: cached.distance };
    }

    // Inizializza se necessario
    if (!(await this.ensureInitialized())) {
      return null;
    }

    return new Promise((resolve) => {
      const directionsService = new window.google.maps.DirectionsService();
      
      const request = {
        origin: { lat: originLat, lng: originLng },
        destination: { lat: DEFAULT_ADDRESS.lat, lng: DEFAULT_ADDRESS.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC
      };

      directionsService.route(request, (result: any, status: any) => {
        if (status === 'OK' && result) {
          const route = result.routes[0];
          const leg = route.legs[0];
          
          const durationInSeconds = leg.duration?.value || 0;
          const durationInMinutes = Math.round(durationInSeconds / 60);
          const distance = leg.distance?.value || 0;
          
          let durationText = '';
          const hours = Math.floor(durationInMinutes / 60);
          const minutes = durationInMinutes % 60;
          
          if (hours > 0) {
            durationText = `${hours}h ${minutes}min`;
          } else {
            durationText = `${minutes}min`;
          }
          
          const distanceKm = (distance / 1000).toFixed(1);
          const result_data = {
            duration: durationText,
            distance: `${distanceKm} km`
          };
          
          // Salva in cache
          travelTimeCache.set(cacheKey, { ...result_data, timestamp: Date.now() });
          console.log('[GoogleMapsService] ✅ Travel Time completato e cached:', cacheKey);
          
          resolve(result_data);
        } else {
          console.warn('[GoogleMapsService] ⚠️ Travel Time fallito:', status);
          resolve(null);
        }
      });
    });
  }

  /**
   * Reverse Geocoding con cache
   */
  async reverseGeocode(lat: number, lng: number): Promise<string | null> {
    const cacheKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    const cached = reverseGeocodingCache.get(cacheKey);
    const now = Date.now();
    
    // Controlla cache
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      console.log('[GoogleMapsService] 💾 Reverse Geocoding dalla cache:', cacheKey);
      return cached.address;
    }

    // Inizializza se necessario
    if (!(await this.ensureInitialized())) {
      return null;
    }

    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      
      geocoder.geocode({ location: { lat, lng } }, (results: any, status: string) => {
        if (status === 'OK' && results[0]) {
          const address = results[0].formatted_address;
          
          // Salva in cache
          reverseGeocodingCache.set(cacheKey, { address, timestamp: Date.now() });
          console.log('[GoogleMapsService] ✅ Reverse Geocoding completato e cached:', cacheKey);
          
          resolve(address);
        } else {
          console.warn('[GoogleMapsService] ⚠️ Reverse Geocoding fallito:', status);
          resolve(null);
        }
      });
    });
  }

  /**
   * Statistiche cache per monitoraggio
   */
  getCacheStats() {
    return {
      geocoding: geocodingCache.size,
      travelTime: travelTimeCache.size,
      autocomplete: autocompleteCache.size,
      placeDetails: placeDetailsCache.size,
      reverseGeocoding: reverseGeocodingCache.size,
      total: geocodingCache.size + travelTimeCache.size + autocompleteCache.size + placeDetailsCache.size + reverseGeocodingCache.size
    };
  }

  /**
   * Pulisce tutte le cache manualmente
   */
  clearAllCaches() {
    geocodingCache.clear();
    travelTimeCache.clear();
    autocompleteCache.clear();
    placeDetailsCache.clear();
    reverseGeocodingCache.clear();
    console.log('[GoogleMapsService] 🧹 Tutte le cache pulite manualmente');
  }
}

// Esporta istanza singleton
export const googleMapsService = GoogleMapsService.getInstance();